/**
 * Tiny global store for live recording status.
 *
 * The recorder lives deep inside the session panel's Toolbar, but the therapist must be
 * able to confirm audio is being captured from ANY tab. The header strip subscribes here
 * instead of prop-drilling recorder internals up through the app shell.
 */
import { useSyncExternalStore } from "react";

export type RecordingStatus = {
  recording: boolean;
  startedAt: number | null;
  chunksUploaded: number;
  chunksLost: number;
  stream: MediaStream | null;
};

let status: RecordingStatus = {
  recording: false,
  startedAt: null,
  chunksUploaded: 0,
  chunksLost: 0,
  stream: null,
};

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function recordingStarted(stream: MediaStream) {
  status = { recording: true, startedAt: Date.now(), chunksUploaded: 0, chunksLost: 0, stream };
  emit();
}

export function recordingStopped() {
  status = { ...status, recording: false, stream: null };
  emit();
}

export function recordingChunkUploaded() {
  status = { ...status, chunksUploaded: status.chunksUploaded + 1 };
  emit();
}

export function recordingChunkLost() {
  status = { ...status, chunksLost: status.chunksLost + 1 };
  emit();
}

export function getRecordingStatus(): RecordingStatus {
  return status;
}

export function subscribeRecordingStatus(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useRecordingStatus(): RecordingStatus {
  return useSyncExternalStore(subscribeRecordingStatus, getRecordingStatus);
}
